import React, { useEffect, useState, useCallback } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '@services/api';
import { ROUTES } from '@/constants';
import { useAuth } from '@contexts/AuthContext';

function daysUntil(dateIso: string): number {
  const t = new Date(dateIso).setHours(0, 0, 0, 0);
  const n = new Date().setHours(0, 0, 0, 0);
  return Math.ceil((t - n) / (86400 * 1000));
}

/**
 * Aviso suave de fatura a vencer (financeiro / topo do app) para admin financeiro ou criador.
 */
const PaymentDueReminderBar: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [text, setText] = useState<string | null>(null);
  const [severity, setSeverity] = useState<'info' | 'warning' | 'error'>('info');
  const load = useCallback(async () => {
    if (!user) return;
    const show = user.permissions?.finance === true || user.roleId?.isAdmin === true;
    if (!show) {
      setText(null);
      return;
    }
    try {
      const [hintsRes, invRes] = await Promise.all([
        apiService.billing.getHints(),
        apiService.billing.listInvoices({ status: 'pending', limit: 1, offset: 0 }),
      ]);
      const daysCfg = hintsRes.data?.data?.dueReminderDays;
      const overdueCfg = hintsRes.data?.data?.overdueReminderDaysAfter;
      const lead =
        Array.isArray(daysCfg) && daysCfg.length > 0 ? Math.max(...daysCfg.map((n) => Number(n) || 0)) : 3;
      const maxOverdueReminderDay =
        Array.isArray(overdueCfg) && overdueCfg.length > 0
          ? Math.max(...overdueCfg.map((n) => Number(n) || 0))
          : null;
      const items = (invRes.data as { items?: Array<{ dueDate?: string; externalId?: string }> })?.items || [];
      const inv = items[0];
      if (!inv?.dueDate) {
        setText(null);
        return;
      }
      const d = daysUntil(String(inv.dueDate));
      if (d > lead) {
        setText(null);
        return;
      }
      if (d < 0) {
        setSeverity('error');
        const extra =
          maxOverdueReminderDay != null && maxOverdueReminderDay > 0
            ? ` O sistema pode enviar lembretes por e-mail e push nos dias configurados após o vencimento (até ${maxOverdueReminderDay} dia${maxOverdueReminderDay > 1 ? 's' : ''}).`
            : '';
        setText(
          `Sua fatura está em atraso. Regularize o pagamento para evitar bloqueio após o período de carência.${extra}`
        );
        return;
      }
      if (d === 0) {
        setSeverity('warning');
        setText('Sua fatura vence hoje. Manter o pagamento em dia garante acesso contínuo ao sistema.');
      } else if (d <= 1) {
        setSeverity('warning');
        setText(`Falta ${d} dia para o vencimento da fatura. Que tal regularizar com tranquilidade?`);
      } else {
        setSeverity('info');
        setText(`Faltam ${d} dias para o vencimento da fatura. Planejar o pagamento agora evita interrupções.`);
      }
    } catch {
      setText(null);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!text) return null;

  return (
    <Alert
      severity={severity}
      sx={{ borderRadius: 0, py: 0.5 }}
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button color="inherit" size="small" onClick={() => navigate(ROUTES.APP_FINANCE)}>
            Financeiro
          </Button>
        </Box>
      }
    >
      {text}
    </Alert>
  );
};

export default PaymentDueReminderBar;
